import { useState, useMemo } from "react";
import { useQuery, useMutation } from "react-query";
import { X, Copy, Search, Check, AlertCircle, Loader2 } from "lucide-react";
import { apiService } from "../services/api";
import toast from "react-hot-toast";

type AssessmentType = "cra" | "sdl" | "cra-compliance";

interface CopyAssessmentResult {
  productId: string;
  productName: string;
  success: boolean;
  error?: string;
}

interface CopyAssessmentResponse {
  sourceProductId: string;
  assessmentType: string;
  totalRequested: number;
  totalSucceeded: number;
  totalFailed: number;
  results: CopyAssessmentResult[];
}

interface CopyAssessmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  sourceProductId: string;
  sourceProductName: string;
  assessmentType: AssessmentType;
  /** Required for SDL assessments */
  period?: string;
}

export default function CopyAssessmentModal({
  isOpen,
  onClose,
  sourceProductId,
  sourceProductName,
  assessmentType,
  period,
}: CopyAssessmentModalProps) {
  const [search, setSearch] = useState("");
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [results, setResults] = useState<CopyAssessmentResponse | null>(null);

  const { data: productsData, isLoading: productsLoading } = useQuery(
    ["products", "copy-targets"],
    () => apiService.getProducts({ pageSize: 1000 }),
    { enabled: isOpen },
  );

  const products = useMemo(() => {
    const all =
      productsData?.data?.data?.products || productsData?.data?.data || [];
    return all.filter((p: any) => p.id !== sourceProductId);
  }, [productsData, sourceProductId]);

  const filteredProducts = useMemo(() => {
    if (!search.trim()) return products;
    const q = search.toLowerCase();
    return products.filter(
      (p: any) =>
        p.name?.toLowerCase().includes(q) ||
        p.businessArea?.toLowerCase().includes(q) ||
        p.division?.toLowerCase().includes(q),
    );
  }, [products, search]);

  const copyMutation = useMutation(
    async () => {
      const targetProductIds = Array.from(selectedIds);
      if (assessmentType === "cra") {
        return apiService.copyCRAAssessment(sourceProductId, targetProductIds);
      } else if (assessmentType === "cra-compliance") {
        return apiService.copyCRAComplianceAssessment(
          sourceProductId,
          targetProductIds,
        );
      } else {
        if (!period) throw new Error("Period is required for SDL copy");
        return apiService.copySDLAssessment(
          sourceProductId,
          period,
          targetProductIds,
        );
      }
    },
    {
      onSuccess: (response) => {
        const data = response.data?.data as CopyAssessmentResponse;
        setResults(data);
        if (data.totalFailed === 0) {
          toast.success(
            `Copied to ${data.totalSucceeded} product(s) successfully`,
          );
        } else {
          toast.error(
            `${data.totalFailed} of ${data.totalRequested} copies failed`,
          );
        }
      },
      onError: (error: any) => {
        toast.error(
          error?.response?.data?.error || "Failed to copy assessment",
        );
      },
    },
  );

  const toggleProduct = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleClose = () => {
    setSearch("");
    setSelectedIds(new Set());
    setResults(null);
    onClose();
  };

  if (!isOpen) return null;

  const typeLabel =
    assessmentType === "cra"
      ? "CRA Applicability"
      : assessmentType === "cra-compliance"
        ? "CRA Compliance"
        : "SDL";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-xl mx-4 max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <Copy className="h-5 w-5" />
              Copy {typeLabel} Answers
            </h2>
            <p className="text-sm text-gray-500 mt-0.5">
              From: <span className="font-medium">{sourceProductName}</span>
              {period && <span className="ml-2">({period})</span>}
            </p>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Results view */}
        {results ? (
          <div className="flex-1 overflow-y-auto px-6 py-4">
            <div className="mb-4">
              <p className="text-sm text-gray-600">
                {results.totalSucceeded} of {results.totalRequested} copied
                successfully
              </p>
            </div>
            <div className="space-y-2">
              {results.results.map((r) => (
                <div
                  key={r.productId}
                  className={`flex items-center gap-3 p-3 rounded-lg border ${
                    r.success
                      ? "border-green-200 bg-green-50"
                      : "border-red-200 bg-red-50"
                  }`}
                >
                  {r.success ? (
                    <Check className="h-4 w-4 text-green-600 flex-shrink-0" />
                  ) : (
                    <AlertCircle className="h-4 w-4 text-red-600 flex-shrink-0" />
                  )}
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {r.productName}
                    </p>
                    {r.error && (
                      <p className="text-xs text-red-600 mt-0.5">{r.error}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <>
            {/* Search */}
            <div className="px-6 py-3 border-b">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search products..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              {selectedIds.size > 0 && (
                <p className="text-xs text-blue-600 mt-2">
                  {selectedIds.size} product(s) selected
                </p>
              )}
            </div>

            {/* Product list */}
            <div className="flex-1 overflow-y-auto px-6 py-2 min-h-[200px] max-h-[400px]">
              {productsLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
                </div>
              ) : filteredProducts.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-8">
                  No products found
                </p>
              ) : (
                <div className="space-y-1">
                  {filteredProducts.map((p: any) => (
                    <label
                      key={p.id}
                      className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors ${
                        selectedIds.has(p.id)
                          ? "bg-blue-50 border border-blue-200"
                          : "border border-transparent"
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={selectedIds.has(p.id)}
                        onChange={() => toggleProduct(p.id)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {p.name}
                        </p>
                        <p className="text-xs text-gray-500">
                          {p.businessArea} · {p.division}
                        </p>
                      </div>
                    </label>
                  ))}
                </div>
              )}
            </div>
          </>
        )}

        {/* Footer */}
        <div className="px-6 py-4 border-t flex justify-end gap-3">
          <button
            onClick={handleClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            {results ? "Close" : "Cancel"}
          </button>
          {!results && (
            <button
              onClick={() => copyMutation.mutate()}
              disabled={selectedIds.size === 0 || copyMutation.isLoading}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {copyMutation.isLoading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Copy className="h-4 w-4" />
              )}
              Copy to {selectedIds.size || ""} Product
              {selectedIds.size !== 1 ? "s" : ""}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
